import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IMG = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || ""}${u}`);

export default function Reviews() {
  const [params, setParams] = useSearchParams();
  const productId = params.get("product_id") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [products, setProducts] = useState([]);
  const LIMIT = 12;

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    const p = productId ? `&product_id=${productId}` : "";
    api.get(`/reviews?limit=${LIMIT}${p}`).then(({ data }) => {
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || { count: 0, average: 0 });
    }).finally(() => setLoading(false));
  }, [productId]);

  const loadMore = async () => {
    const p = productId ? `&product_id=${productId}` : "";
    const next = offset + LIMIT;
    const { data } = await api.get(`/reviews?limit=${LIMIT}&offset=${next}${p}`);
    setItems((prev) => [...prev, ...(data.items || [])]);
    setOffset(next);
  };

  const setProductFilter = (val) => {
    if (val === "__all__") setParams({});
    else setParams({ product_id: val });
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-semibold mb-2">Reviews</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#1A1C1A]">The Collective, in their own words</h1>
        {summary.count > 0 && (
          <div className="mt-4 flex items-center gap-3">
            <StarRating value={Math.round(summary.average)} size={22} />
            <span className="text-sm text-stone-600">{summary.average.toFixed(1)} · {summary.count} review{summary.count === 1 ? "" : "s"}</span>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500">Filter by product</label>
          <Select value={productId || "__all__"} onValueChange={setProductFilter}>
            <SelectTrigger className="w-64" data-testid="reviews-product-filter"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All products</SelectItem>
              {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        {loading && <p className="mt-12 text-stone-500 italic">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="mt-12 py-16 text-center border border-dashed border-[#E5E2DC] rounded-xl">
            <p className="font-serif text-2xl font-light italic text-stone-500">No reviews yet.</p>
            <Link to="/shop" className="mt-4 inline-block text-xs uppercase tracking-[0.2em] text-[#1A1C1A] hover:underline">Shop the collection →</Link>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((r) => (
              <article key={r.id} className="bg-white border border-[#E5E2DC] rounded-2xl p-6" data-testid="reviews-page-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#1A1C1A] text-[#F9F9F6] flex items-center justify-center font-serif text-sm">{r.author_initial}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1A1C1A] truncate">{r.author_name}</p>
                    {r.verified_purchase && <p className="text-[10px] uppercase tracking-[0.15em] text-[#3F5F3F]">Verified purchase</p>}
                  </div>
                  <span className="text-[11px] text-stone-400">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                </div>
                <StarRating value={r.rating} />
                {r.title && <h3 className="mt-3 font-serif text-xl font-light">{r.title}</h3>}
                <p className="mt-2 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                {r.image_urls?.length > 0 && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    {r.image_urls.map((u, i) => (
                      <a key={i} href={IMG(u)} target="_blank" rel="noreferrer">
                        <img src={IMG(u)} alt="" className="w-20 h-20 object-cover rounded-md" />
                      </a>
                    ))}
                  </div>
                )}
                {r.owner_reply && (
                  <div className="mt-4 border-l-2 border-[#1A1C1A] pl-4 py-2 bg-[#F4F1EA] rounded-r">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#3F5F3F] font-semibold mb-1">Reply from Jenova</p>
                    <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{r.owner_reply}</p>
                  </div>
                )}
                {r.product_name && (
                  <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-stone-400">on {r.product_name}</p>
                )}
              </article>
            ))}
          </div>
        )}
        {!loading && items.length > 0 && items.length < total && (
          <div className="mt-10 text-center">
            <Button variant="outline" onClick={loadMore} className="rounded-full">Load more reviews</Button>
          </div>
        )}
      </div>
    </div>
  );
}
